import Phaser from 'phaser';
import { ASSET_KEYS, ASSET_PATHS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { bookCatalog } from '@/data/bookCatalog';
import { courseCatalog } from '@/data/courseCatalog';
import { eventKatalog } from '@/data/eventCatalog';
import { konkurrentKatalog, type KonkurrentRaw } from '@/data/konkurrenter';
import { tidligSpillKatalog } from '@/data/tidligSpillKatalog';
import { lastKaffeprat, type KaffepratData } from '@/systems/kaffeprat';
import { alleForfatterPortretter } from '@/data/portretter';
import { gameState } from '@/state/store';
import { bootstrapBjornArvHvisNy } from '@/state/bjornArv';
import { lottieOverlay, type LottieHandle } from '@/ui/LottieOverlay';
import type { BookRaw, CourseRaw, EventRaw } from '@/data/types';

const BOOKS_URL = 'data/books.json';
const COURSES_URL = 'data/courses.json';
const EVENTS_URL = 'data/events.json';
const KONKURRENTER_URL = 'data/konkurrenter.json';
const TIDLIGSPILL_URL = 'data/tidligspill.json';
const KAFFEPRAT_URL = 'data/kaffeprat.json';

export class PreloadScene extends Phaser.Scene {
  private lottieHandle?: LottieHandle | null;
  private watchdog?: number;
  private startetForlagshuset = false;

  constructor() {
    super('Preload');
  }

  preload(): void {
    this.cameras.main.setBackgroundColor(COLORS.beige);

    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;

    // Lottie-loading-spinner over progress-baren (auto-cleanup ved scene-start)
    try {
      this.lottieHandle = lottieOverlay.spill(this, cx, cy + 240, 'loading-books', {
        width: 200,
        height: 200,
        loop: true,
        zIndex: 850,
      });
    } catch (err) {
      console.warn('[preload] Lottie-spinner feilet, fortsetter uten', err);
      this.lottieHandle = null;
    }

    // Watchdog: tvinger scene-overgang etter 12 sek hvis preload/create henger.
    // Bedre å starte spillet med tomme kataloger enn å henge for alltid på
    // "laster manuskripter".
    if (typeof window !== 'undefined') {
      this.watchdog = window.setTimeout(() => {
        if (this.startetForlagshuset) return;
        console.warn('[preload] Watchdog tvinger scene-overgang etter 12s');
        this.startForlagshuset();
      }, 12000);
    }

    this.add
      .text(cx, cy - 60, 'Forlagshuset', {
        fontFamily: FONTS.display,
        fontSize: fontStr(88),
        color: '#862736',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, cy + 20, 'laster manuskripter …', {
        fontFamily: FONTS.body,
        fontSize: fontStr(34),
        color: '#1a1a1a',
      })
      .setOrigin(0.5);

    const barW = 520;
    const barH = 14;
    const barX = cx - barW / 2;
    const barY = cy + 90;

    const frame = this.add.rectangle(cx, barY + barH / 2, barW + 8, barH + 8, 0x000000, 0);
    frame.setStrokeStyle(2, COLORS.ink, 0.6);

    const fill = this.add.rectangle(barX, barY, 1, barH, COLORS.wine).setOrigin(0, 0);

    this.load.on('progress', (value: number) => {
      fill.width = Math.max(1, barW * value);
    });

    // Sett XHR-timeout på Phaser-loader så ingen enkelt-fil kan blokkere
    // preload-fasen i flere minutter ved server-stall. Watchdog griper
    // inn dersom hele loader-flyten henger.
    const xhrSettings = (this.load as unknown as { xhr?: { timeout: number } }).xhr;
    if (xhrSettings) xhrSettings.timeout = 5000;

    for (const [key, path] of Object.entries(ASSET_PATHS)) {
      this.load.image(key, path);
    }

    for (const portrett of alleForfatterPortretter()) {
      this.load.image(portrett.key, portrett.path);
    }

    // Walk-cycle for korridor-avataren (AutoSprite, 8 frames à 512px).
    // Mangler fila faller KorridorScene tilbake til prosedyre-gange.
    this.load.spritesheet(ASSET_KEYS.playerWalk, 'assets/characters/player_walk_01.png', {
      frameWidth: 512,
      frameHeight: 512,
    });

    const EVENT_BILDER: Array<{ key: string; path: string }> = [
      { key: 'event_brage-nominasjon', path: 'assets/events/event_brage-nominasjon_01.webp' },
      { key: 'event_brage-vinner', path: 'assets/events/event_brage-vinner_01.webp' },
      { key: 'event_plagiat-skandale', path: 'assets/events/event_plagiat-skandale_01.webp' },
      { key: 'event_telefonen-ringer', path: 'assets/events/event_telefonen-ringer_01.webp' },
      { key: 'event_nytt-manus-i-posten', path: 'assets/events/event_nytt-manus-i-posten_01.webp' },
    ];
    for (const b of EVENT_BILDER) {
      this.load.image(b.key, b.path);
    }

    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      // Manglende portretter/events er ikke kritisk — fallback håndteres i UI
      console.warn(`Lastefeil: ${file.key} (${file.url})`);
    });

    this.load.json('books-json', BOOKS_URL);
    this.load.json('courses-json', COURSES_URL);
    this.load.json('events-json', EVENTS_URL);
    this.load.json('konkurrenter-json', KONKURRENTER_URL);
    this.load.json('tidligspill-json', TIDLIGSPILL_URL);
    this.load.json('kaffeprat-json', KAFFEPRAT_URL);
  }

  async create(): Promise<void> {
    const raw = this.cache.json.get('books-json') as BookRaw[] | undefined;
    if (Array.isArray(raw)) {
      bookCatalog.loadFromArray(raw);
    } else {
      try {
        await medTimeout(bookCatalog.loadFromUrl(BOOKS_URL), 4000, 'books');
      } catch (err) {
        console.error('Klarte ikke laste bok-katalog', err);
      }
    }

    const rawCourses = this.cache.json.get('courses-json') as CourseRaw[] | undefined;
    if (Array.isArray(rawCourses)) {
      courseCatalog.loadFromArray(rawCourses);
    } else {
      try {
        await medTimeout(courseCatalog.load(this), 4000, 'courses');
      } catch (err) {
        console.error('Klarte ikke laste kurs-katalog', err);
      }
    }

    const rawEvents = this.cache.json.get('events-json') as EventRaw[] | undefined;
    if (Array.isArray(rawEvents)) {
      eventKatalog.loadFromArray(rawEvents);
    } else {
      try {
        const data = await hentJsonMedTimeout<EventRaw[]>(EVENTS_URL, 4000);
        if (Array.isArray(data)) eventKatalog.loadFromArray(data);
      } catch (err) {
        console.warn('Klarte ikke laste event-katalog', err);
      }
    }

    const rawKonk = this.cache.json.get('konkurrenter-json') as KonkurrentRaw[] | undefined;
    if (Array.isArray(rawKonk)) {
      konkurrentKatalog.loadFromArray(rawKonk);
    } else {
      try {
        const data = await hentJsonMedTimeout<KonkurrentRaw[]>(KONKURRENTER_URL, 4000);
        if (Array.isArray(data)) konkurrentKatalog.loadFromArray(data);
      } catch (err) {
        console.warn('Klarte ikke laste konkurrent-katalog', err);
      }
    }

    const rawTidlig = this.cache.json.get('tidligspill-json') as EventRaw[] | undefined;
    if (Array.isArray(rawTidlig)) {
      tidligSpillKatalog.loadFromArray(rawTidlig);
    } else {
      try {
        const data = await hentJsonMedTimeout<EventRaw[]>(TIDLIGSPILL_URL, 4000);
        if (Array.isArray(data)) tidligSpillKatalog.loadFromArray(data);
      } catch (err) {
        console.warn('Klarte ikke laste tidlig-spill-katalog', err);
      }
    }

    const rawKaffe = this.cache.json.get('kaffeprat-json') as KaffepratData | undefined;
    if (rawKaffe && typeof rawKaffe === 'object') {
      lastKaffeprat(rawKaffe);
    } else {
      try {
        const data = await hentJsonMedTimeout<KaffepratData>(KAFFEPRAT_URL, 4000);
        if (data) lastKaffeprat(data);
      } catch (err) {
        console.warn('Klarte ikke laste kaffeprat', err);
      }
    }

    try {
      bootstrapBjornArvHvisNy(gameState);
      gameState.bootstrapInnboksHvisTom();
    } catch (err) {
      console.warn('[preload] bootstrap feilet', err);
    }

    this.startForlagshuset();
  }

  private startForlagshuset(): void {
    if (this.startetForlagshuset) return;
    this.startetForlagshuset = true;

    if (this.watchdog !== undefined) {
      window.clearTimeout(this.watchdog);
      this.watchdog = undefined;
    }

    try {
      this.lottieHandle?.destroy();
    } catch (err) {
      console.warn('[preload] lottieHandle.destroy feilet', err);
    }
    this.lottieHandle = undefined;

    this.scene.start('Forlagshuset');
    this.scene.launch('HUD');
  }
}

function medTimeout<T>(p: Promise<T>, ms: number, navn: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${navn} timeout etter ${ms}ms`)), ms),
    ),
  ]);
}

async function hentJsonMedTimeout<T>(url: string, ms: number): Promise<T | null> {
  if (typeof fetch === 'undefined') return null;
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timer = ctrl
    ? setTimeout(() => {
        try {
          ctrl.abort();
        } catch {
          /* ignore */
        }
      }, ms)
    : undefined;
  try {
    const res = await fetch(url, ctrl ? { signal: ctrl.signal } : undefined);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

void ASSET_KEYS;
